import { useEffect, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Card, FormActions, Input, Select, Textarea } from '@/components/ui'
import { useCreateCustomer, useUpdateCustomer } from '@/hooks/useCustomers'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { DEFAULT_CUSTOMER_SOURCES } from '@/hooks/useSourceSettings'
import type { Customer } from '@/types'
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning'
import { useSupportSession } from '@/hooks/useSupportSession'
import { lockedInSession } from '@/lib/sessionRedaction'

// EP-160: the customer's tenant personal data (the registry's c2 and c3
// columns of customers that this form edits).
type TenantField = 'first_name' | 'last_name' | 'phone' | 'email' | 'id_card_number' | 'tax_id' | 'address'

interface CustomerFormData {
  first_name: string
  last_name: string
  phone: string
  email: string
  id_card_number: string
  tax_id: string
  address: string
  status: 'active' | 'alumni' | 'prospect'
  source: string
}

export interface CustomerFieldsProps {
  /** Null → create mode (POST). Non-null → edit mode (PATCH). */
  customer: Customer | null
  /** Called with the saved Customer after a successful POST or PATCH. */
  onSave: (saved: Customer) => void
  /**
   * When true, only Essentials (first_name + phone) are visible inline;
   * the remaining fields collapse into an "Advanced options" disclosure.
   * Auto-focuses first_name. Intended for the onboarding wizard (S2-1).
   * Must NOT be piggybacked on formId — they are independent concerns.
   * Default: false.
   */
  streamlinedMode?: boolean
  /** Fires with the mutation-pending state so the wizard footer Save can spin. */
  onSubmittingChange?: (submitting: boolean) => void
  /**
   * Optional HTML form id. When set, the <form> element receives this id so an
   * external <button form={formId} type="submit"> can trigger submission.
   */
  formId?: string
  /**
   * Apply-from-file banner node (task-030: ApplyFromFileButton).
   * Rendered at the very top of the card, before the first input.
   * Pass null or omit to show nothing.
   */
  prefillBanner?: ReactNode
  /**
   * When provided, resets the RHF form with these values so the fields
   * display the AI-parsed data (task-030 Apply path).
   */
  prefillDefaults?: Partial<CustomerFormData>
  className?: string
}

/**
 * Self-contained customer form.
 *
 * Owns its own useForm instance and mutation hooks so it can be embedded
 * in any context (CustomerFormPage, onboarding wizard, quick-add modal).
 *
 * When customer === null the form is blank and Save fires POST /api/customers.
 * When customer is provided the form pre-fills and Save fires PATCH /api/customers/:id.
 * On success onSave(savedCustomer) is called so the parent can navigate/close.
 *
 * Field grouping (S2-1 / EP-86):
 *   Essentials: first_name, phone (always visible)
 *   Advanced — Contact (last_name, email) · IDs (id_card_number, tax_id)
 *             · Classification (status, source) · address
 *   In streamlinedMode the Advanced group collapses into a disclosure.
 */
export function CustomerFields({
  customer,
  onSave,
  formId,
  streamlinedMode = false,
  prefillBanner,
  prefillDefaults,
  onSubmittingChange,
  className,
}: CustomerFieldsProps) {
  const { t, i18n } = useTranslation('customers')
  const { t: tc } = useTranslation('common')

  const lang = i18n.language?.startsWith('th') ? 'th' : 'en'

  const translateError = useApiErrorTranslator('customers')
  const addToast = useNotificationStore((s) => s.addToast)

  const createCustomer = useCreateCustomer()
  const updateCustomer = useUpdateCustomer()

  // EP-160 section 2.2 and S3: in a FlowRent support session a field the
  // server masked ("ส•••", "xxx-xxx-1234") or removed (ID card number, tax
  // id) is disabled and never sent back (the server would answer 422
  // masked_value_in_write, and a blank sent back would clear it). The setup
  // scope writes no tenant data at all (422 scope_setup_no_tenants), so every
  // tenant field is disabled there and a new customer cannot be saved.
  const supportSession = useSupportSession()
  const setupScope = supportSession.active && supportSession.scope === 'setup'
  const locked = (field: TenantField) => setupScope || lockedInSession(customer, field)
  const lockedHint = (field: TenantField) => (locked(field) ? tc('support_session.field_locked_hint') : undefined)

  // Report mutation-pending so the wizard footer Save can show a spinner.
  useEffect(() => {
    onSubmittingChange?.(createCustomer.isPending || updateCustomer.isPending)
  }, [createCustomer.isPending, updateCustomer.isPending, onSubmittingChange])

  const { data: account } = useAccountSettings()

  const statusOptions = [
    { value: 'active', label: t('status.active') },
    { value: 'alumni', label: t('status.alumni') },
    { value: 'prospect', label: t('status.prospect') },
  ]

  // Resolve sources: account-configured or fall back to defaults
  const sources =
    account?.settings.customer_sources?.length
      ? account.settings.customer_sources
      : DEFAULT_CUSTOMER_SOURCES

  const sourceOptions = [
    { value: '', label: t('placeholders.source') },
    ...sources.map((s) => ({ value: s.key, label: lang === 'th' ? s.th : s.en })),
  ]

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<CustomerFormData>({
    values: customer
      ? {
          first_name: customer.first_name ?? '',
          last_name: customer.last_name ?? '',
          phone: customer.phone ?? '',
          email: customer.email ?? '',
          id_card_number: customer.id_card_number ?? '',
          tax_id: customer.tax_id ?? '',
          address: customer.address ?? '',
          // 'external' status is not a valid form value — fall back to prospect
          status: customer.status === 'external' ? 'prospect' : customer.status,
          source: customer.source ?? '',
        }
      : {
          first_name: '',
          last_name: '',
          phone: '',
          email: '',
          id_card_number: '',
          tax_id: '',
          address: '',
          status: 'prospect',
          source: '',
        },
  })

  // WEB-M3: warn before tab close/refresh with unsaved edits (beforeunload).
  useUnsavedChangesWarning(isDirty)

  // When the wizard passes prefillDefaults (Apply from file path), reset the
  // form with those values so the user sees them pre-filled immediately.
  useEffect(() => {
    if (prefillDefaults && Object.keys(prefillDefaults).length > 0) {
      reset((current) => ({ ...current, ...prefillDefaults }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillDefaults])

  const onSubmit = async (data: CustomerFormData) => {
    // Guard: at least one identifying field must be set or we'd POST an empty
    // record that surfaces as "Unnamed" everywhere downstream (lease picker,
    // invoices, exports). Cheaper to reject here than to clean up later.
    const hasIdentifier = Boolean(
      data.first_name?.trim() ||
        data.last_name?.trim() ||
        data.phone?.trim() ||
        data.email?.trim(),
    )
    if (!hasIdentifier) {
      // EP-158 AC-9 (F11): streamlinedMode (the wizard's step 4) hides the
      // email field entirely (display:none, no toggle — see the Advanced
      // section below), so the standalone message's "...or email" names a
      // field the operator can't see on this screen.
      addToast({
        type: 'error',
        message: t(
          streamlinedMode ? 'messages.identifier_required_streamlined' : 'messages.identifier_required',
        ),
      })
      return
    }

    const payload = {
      ...(locked('first_name') ? {} : { first_name: data.first_name || undefined }),
      ...(locked('last_name') ? {} : { last_name: data.last_name || undefined }),
      ...(locked('phone') ? {} : { phone: data.phone || undefined }),
      ...(locked('email') ? {} : { email: data.email || undefined }),
      ...(locked('id_card_number') ? {} : { id_card_number: data.id_card_number || undefined }),
      ...(locked('tax_id') ? {} : { tax_id: data.tax_id || null }),
      ...(locked('address') ? {} : { address: data.address || undefined }),
      status: data.status,
      // Send null to clear source, undefined to omit when blank
      source: data.source || null,
    }

    try {
      if (customer) {
        const saved = await updateCustomer.mutateAsync({ id: customer.id, ...payload })
        onSave(saved)
      } else {
        const saved = await createCustomer.mutateAsync(payload)
        onSave(saved)
      }
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const isEdit = !!customer

  // ── Advanced section fields ─────────────────────────────────────────────
  // Always rendered in the DOM (display:none in streamlinedMode when closed)
  // to preserve RHF registration and default values (S2-2).

  const advancedFields = (
    <div className="space-y-6">
      {/* Contact sub-group */}
      {!streamlinedMode && (
        <p className="text-sm font-medium text-base-content/60">{t('sections.contact')}</p>
      )}
      <div className="grid gap-6 md:grid-cols-2">
        <Input
          label={t('labels.last_name')}
          placeholder={t('placeholders.last_name')}
          error={errors.last_name?.message}
          disabled={locked('last_name')}
          hint={lockedHint('last_name')}
          {...register('last_name')}
        />
        <Input
          label={t('labels.email')}
          type="email"
          placeholder={t('placeholders.email')}
          error={errors.email?.message}
          disabled={locked('email')}
          hint={lockedHint('email')}
          {...register('email')}
        />
      </div>

      {/* IDs sub-group */}
      {!streamlinedMode && (
        <p className="text-sm font-medium text-base-content/60">{t('sections.ids')}</p>
      )}
      <div className="grid gap-6 md:grid-cols-2">
        <Input
          label={t('labels.id_card_number')}
          placeholder={t('placeholders.id_card_number')}
          error={errors.id_card_number?.message}
          disabled={locked('id_card_number')}
          hint={lockedHint('id_card_number')}
          {...register('id_card_number')}
        />
        <Input
          label={t('labels.tax_id')}
          placeholder={t('placeholders.tax_id')}
          maxLength={13}
          error={errors.tax_id?.message}
          disabled={locked('tax_id')}
          hint={lockedHint('tax_id')}
          {...register('tax_id', {
            maxLength: { value: 13, message: tc('errors.max_length', { max: 13 }) },
          })}
        />
      </div>

      {/* Classification sub-group */}
      {!streamlinedMode && (
        <p className="text-sm font-medium text-base-content/60">{t('sections.classification')}</p>
      )}
      <div className="grid gap-6 md:grid-cols-2">
        <Select
          label={t('labels.status')}
          options={statusOptions}
          {...register('status')}
        />
        <Select
          label={t('labels.source')}
          options={sourceOptions}
          {...register('source')}
        />
      </div>

      <Textarea
        label={t('labels.address')}
        placeholder={t('placeholders.address')}
        rows={3}
        disabled={locked('address')}
        hint={lockedHint('address')}
        {...register('address')}
      />
    </div>
  )

  return (
    <Card className={className}>
      {/* Apply-from-file banner (task-030) — rendered above first input */}
      {prefillBanner}
      {setupScope && (
        <Alert variant="info" className="mb-6" data-testid="customer-setup-scope-notice">
          {tc('support_session.setup_no_tenants_notice')}
        </Alert>
      )}
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-6">

        {/* ── Essentials ──────────────────────────────────────── */}
        {/* In the wizard (streamlinedMode) stack the fields vertically full-width
            to match the property form; standalone pages keep the 2-col grid. */}
        <div className={streamlinedMode ? 'space-y-4' : 'grid gap-6 md:grid-cols-2'}>
          <Input
            label={t('labels.first_name')}
            placeholder={t('placeholders.first_name')}
            error={errors.first_name?.message}
            data-tour-target="customer-first-name"
            // Auto-focus the first field in wizard (streamlined) mode
            autoFocus={streamlinedMode}
            disabled={locked('first_name')}
            hint={lockedHint('first_name')}
            {...register('first_name')}
          />
          <Input
            label={t('labels.phone')}
            placeholder={t('placeholders.phone')}
            error={errors.phone?.message}
            disabled={locked('phone')}
            hint={lockedHint('phone')}
            {...register('phone')}
          />
        </div>

        {/* ── Advanced ───────────────────────────────────────── */}
        {streamlinedMode ? (
          // Wizard mode (step 4): Advanced is hidden entirely — no toggle — so
          // onboarding stays essentials-only (name + phone). Kept in the DOM
          // (display:none) so RHF keeps last_name/email/id_card/tax_id
          // registered with their empty defaults; they're editable later.
          <div id="customer-advanced-options" style={{ display: 'none' }}>
            {advancedFields}
          </div>
        ) : (
          // Standalone mode (create/edit pages): all fields visible with sub-section headings
          advancedFields
        )}

        {/* In-form submit is hidden in wizard mode. When the wizard supplies
            `formId`, OnboardingBottomBar's "Save & Continue" submits via
            <button form={formId} type="submit">; an in-form duplicate
            confused users (per user feedback). */}
        {!formId && (
          <FormActions>
            <Button
              type="submit"
              loading={createCustomer.isPending || updateCustomer.isPending}
              disabled={setupScope && !isEdit}
            >
              {isEdit ? tc('actions.save_changes') : t('actions.create_customer')}
            </Button>
          </FormActions>
        )}
      </form>
    </Card>
  )
}
